import React, { useState, useEffect } from "react";
import {
  Dialog,
  Tabs,
  Box,
  Button,
  Flex,
  Select,
  Text,
  Badge,
  ScrollArea,
  Switch,
  TextField,
  Card,
  IconButton,
  Table,
  Grid,
} from "@radix-ui/themes";
import {
  Play,
  Trash2,
  History as HistoryIcon,
  FileSpreadsheet,
  Sparkles,
  Clock,
  AlertCircle,
  Loader2,
  ArrowLeft,
  FileCode,
} from "lucide-react";
import Papa from "papaparse";
import toast from "react-hot-toast";

import { fileService } from "@/services/breeze/FileService";
import importService, {
  ImportJob,
  ImportMapping,
} from "@/services/breeze/ImportService";
import FileUploadZone from "@/components/files/FileUploadZone";
import { IEntity } from "@/types/breeze/Entity";
import { formatFileSize } from "@/utils/fileUtils";

interface ImportDialogProps {
  entityName: string;
  entityDefinition: IEntity;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onImportCompleted?: () => void;
}

let activeFolderCreationPromise: Promise<number | null> | null = null;

export default function ImportDialog({
  entityName,
  entityDefinition,
  open,
  onOpenChange,
  onImportCompleted,
}: ImportDialogProps) {
  const [tabValue, setTabValue] = useState<string>("history");

  // Jobs and Mappings State
  const [jobs, setJobs] = useState<ImportJob[]>([]);
  const [mappings, setMappings] = useState<ImportMapping[]>([]);
  const [isLoadingJobs, setIsLoadingJobs] = useState(false);
  const [isLoadingMappings, setIsLoadingMappings] = useState(false);
  const [viewingJobLog, setViewingJobLog] = useState<ImportJob | null>(null);

  // New Import Form State
  const [newImportStep, setNewImportStep] = useState<"upload" | "map">(
    "upload",
  );
  const [selectedFile, setSelectedFile] = useState<globalThis.File | null>(
    null,
  );
  const [fileId, setFileId] = useState<number | null>(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<number | null>(null);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [csvHeaders, setCsvHeaders] = useState<string[]>([]);
  const [fieldMappings, setFieldMappings] = useState<Record<string, string>>(
    {},
  );
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [templateName, setTemplateName] = useState("");
  const [selectedTemplateId, setSelectedTemplateId] =
    useState<string>("custom");
  const [isRunningImport, setIsRunningImport] = useState(false);
  const [importsFolderId, setImportsFolderId] = useState<number | null>(null);

  const getOrCreateImportsFolder = async (): Promise<number | null> => {
    if (activeFolderCreationPromise) {
      return activeFolderCreationPromise;
    }

    const run = async (): Promise<number | null> => {
      try {
        const folders = await fileService.getFolders();
        const existing = folders.find(
          (f) =>
            f.name.toLowerCase() === "imports" && f.parent_folder_id === null,
        );
        if (existing) {
          setImportsFolderId(existing.id);
          return existing.id;
        } else {
          const created = await fileService.createFolder(
            {
              name: "Imports",
              parent_folder_id: null,
            },
            true,
          );
          setImportsFolderId(created.id);
          return created.id;
        }
      } catch (err) {
        console.error("Failed to get or create 'Imports' folder:", err);
        // Attempt recovery in case of race condition / transient error
        try {
          const folders = await fileService.getFolders();
          const existing = folders.find(
            (f) =>
              f.name.toLowerCase() === "imports" && f.parent_folder_id === null,
          );
          if (existing) {
            setImportsFolderId(existing.id);
            return existing.id;
          }
        } catch (innerErr) {
          console.error("Failed to recover imports folder search:", innerErr);
        }
        return null;
      } finally {
        activeFolderCreationPromise = null;
      }
    };

    activeFolderCreationPromise = run();
    return activeFolderCreationPromise;
  };

  // Filter out system fields and relationship collections that can't be mapped
  const eligibleFields = entityDefinition.fields.filter(
    (f) =>
      f.name !== "id" &&
      f.name !== "created_at" &&
      f.name !== "updated_at" &&
      f.name !== "tenant_id" &&
      f.database_type !== "one-to-many" &&
      f.database_type !== "many-to-many",
  );

  const fetchJobs = async () => {
    setIsLoadingJobs(true);
    try {
      const data = await importService.getJobs();
      const filtered = data
        .filter((j) => j.entity_name === entityName)
        .sort((a, b) => b.id - a.id);
      setJobs(filtered);
    } catch (err) {
      console.error("Failed to fetch jobs:", err);
    } finally {
      setIsLoadingJobs(false);
    }
  };

  const fetchMappings = async () => {
    setIsLoadingMappings(true);
    try {
      const data = await importService.getMappings();
      const filtered = data.filter((m) => m.entity_name === entityName);
      setMappings(filtered);
    } catch (err) {
      console.error("Failed to fetch mappings:", err);
    } finally {
      setIsLoadingMappings(false);
    }
  };

  useEffect(() => {
    if (!open) return;
    fetchJobs();
    fetchMappings();
    getOrCreateImportsFolder();

    // Reset state when dialog opens
    setNewImportStep("upload");
    setSelectedFile(null);
    setFileId(null);
    setCsvHeaders([]);
    setFieldMappings({});
    setSaveAsTemplate(false);
    setTemplateName("");
    setSelectedTemplateId("custom");
    setViewingJobLog(null);
  }, [open, entityName]);

  // Poll active jobs list
  useEffect(() => {
    if (!open) return;
    const hasActiveJobs = jobs.some(
      (j) => j.status === "Pending" || j.status === "Processing",
    );
    if (!hasActiveJobs) return;

    const interval = setInterval(() => {
      fetchJobs();
    }, 3000);

    return () => clearInterval(interval);
  }, [open, jobs]);

  // Poll detailed job log if active
  useEffect(() => {
    if (!open || !viewingJobLog) return;
    if (
      viewingJobLog.status !== "Pending" &&
      viewingJobLog.status !== "Processing"
    )
      return;

    const interval = setInterval(async () => {
      try {
        const updated = await importService.getJobById(viewingJobLog.id);
        setViewingJobLog(updated);
        setJobs((prev) => prev.map((j) => (j.id === updated.id ? updated : j)));
      } catch (err) {
        console.error("Failed to poll job log:", err);
      }
    }, 3000);

    return () => clearInterval(interval);
  }, [open, viewingJobLog]);

  const handleDeleteMapping = async (id: number) => {
    try {
      await importService.deleteMapping(id);
      toast.success("Mapping template deleted");
      fetchMappings();
    } catch (err: any) {
      toast.error(err.message || "Failed to delete mapping template");
    }
  };

  const handleFileChange = async (files: globalThis.File[]) => {
    if (files.length === 0) return;
    const file = files[0];
    setSelectedFile(file);
    setUploadProgress(0);
    setUploading(true);
    setUploadError(null);

    // Parse headers locally
    Papa.parse(file, {
      preview: 1,
      complete: (results) => {
        if (results.data && results.data.length > 0) {
          const headers = results.data[0] as string[];
          setCsvHeaders(headers);
        } else {
          setUploadError("Could not parse headers from CSV file.");
          setUploading(false);
        }
      },
      error: (err) => {
        setUploadError(`CSV parsing error: ${err.message}`);
        setUploading(false);
      },
    });

    // Upload to file browser
    try {
      let folderId = importsFolderId;
      if (!folderId) {
        folderId = await getOrCreateImportsFolder();
      }

      const formData = new FormData();
      formData.append("file", file);
      const uploadedFile = await fileService.create(
        formData,
        false,
        undefined,
        undefined,
        folderId ?? undefined,
        (percent) => setUploadProgress(percent),
      );
      setFileId(uploadedFile.id);
      setNewImportStep("map");
      toast.success("File uploaded successfully");
    } catch (err: any) {
      setUploadError(err.message || "Failed to upload file");
    } finally {
      setUploading(false);
      setUploadProgress(null);
    }
  };

  const handleTemplateSelect = (templateIdVal: string) => {
    setSelectedTemplateId(templateIdVal);
    if (templateIdVal === "custom") {
      setFieldMappings({});
      return;
    }
    const template = mappings.find((m) => m.id.toString() === templateIdVal);
    if (template) {
      try {
        const parsed = JSON.parse(template.mapping_json);
        const loadedMappings: Record<string, string> = {};
        Object.entries(parsed).forEach(([csvHeader, fieldName]) => {
          loadedMappings[fieldName as string] = csvHeader;
        });
        setFieldMappings(loadedMappings);
      } catch (err) {
        console.error("Failed to parse mapping template:", err);
        toast.error("Failed to load mapping template.");
      }
    }
  };

  const handleAutoMatch = () => {
    const cleanString = (str: string) =>
      str.toLowerCase().replace(/[^a-zA-Z0-9]/g, "");
    const autoMatched: Record<string, string> = {};

    eligibleFields.forEach((field) => {
      const fieldLabel = field.label || field.name;
      const match = csvHeaders.find(
        (h) =>
          cleanString(h) === cleanString(field.name) ||
          cleanString(h) === cleanString(fieldLabel),
      );
      if (match) {
        autoMatched[field.name] = match;
      }
    });

    setFieldMappings((prev) => ({ ...prev, ...autoMatched }));
    toast.success(
      `Matched ${Object.keys(autoMatched).length} fields automatically`,
    );
  };

  const handleMappingChange = (fieldName: string, csvHeader: string) => {
    setFieldMappings((prev) => ({
      ...prev,
      [fieldName]: csvHeader,
    }));
  };

  const handleRunImport = async () => {
    if (!fileId) return;

    const mappingJson: Record<string, string> = {};
    Object.entries(fieldMappings).forEach(([fieldName, header]) => {
      if (header && header !== "__skip__") {
        mappingJson[header] = fieldName;
      }
    });

    if (Object.keys(mappingJson).length === 0) {
      toast.error("Please map at least one field to import");
      return;
    }

    // Check if any required fields are missing
    const missingRequired = eligibleFields.filter(
      (f) => f.is_required && !fieldMappings[f.name],
    );

    if (missingRequired.length > 0) {
      const missingLabels = missingRequired
        .map((f) => f.label || f.name)
        .join(", ");
      toast.error(`Required fields missing mapping: ${missingLabels}`);
      return;
    }

    setIsRunningImport(true);

    try {
      let mappingId: number | null = null;

      if (saveAsTemplate) {
        if (!templateName.trim()) {
          toast.error("Please enter a template name");
          setIsRunningImport(false);
          return;
        }
        const createdMapping = await importService.createMapping({
          name: templateName.trim(),
          entity_name: entityName,
          mapping_json: JSON.stringify(mappingJson),
        });
        mappingId = createdMapping.id;
      }

      await importService.createJob({
        entity_name: entityName,
        file_id: fileId,
        import_mapping_id: mappingId,
        mapping_json: JSON.stringify(mappingJson),
      });

      toast.success("Import job started successfully");

      // Reset
      setSelectedFile(null);
      setFileId(null);
      setFieldMappings({});
      setSaveAsTemplate(false);
      setTemplateName("");
      setNewImportStep("upload");

      onImportCompleted?.();
      setTabValue("history");
      fetchJobs();
    } catch (err: any) {
      toast.error(err.message || "Failed to start import job");
    } finally {
      setIsRunningImport(false);
    }
  };

  const renderStatusBadge = (status: string) => {
    switch (status.toLowerCase()) {
      case "pending":
        return <Badge color="amber">Pending</Badge>;
      case "processing":
        return <Badge color="blue">Processing</Badge>;
      case "completed":
        return <Badge color="green">Completed</Badge>;
      case "failed":
        return <Badge color="red">Failed</Badge>;
      default:
        return <Badge color="gray">{status}</Badge>;
    }
  };

  return (
    <Dialog.Root open={open} onOpenChange={onOpenChange}>
      <Dialog.Content style={{ maxWidth: 680 }}>
        <Dialog.Title>Import {entityDefinition.name}</Dialog.Title>
        <Dialog.Description mb="4">
          Import records from a CSV file using column mappings.
        </Dialog.Description>

        {viewingJobLog ? (
          <Flex direction="column" gap="4" style={{ height: 450 }}>
            <Flex justify="between" align="center">
              <Button variant="ghost" onClick={() => setViewingJobLog(null)}>
                <ArrowLeft size={16} /> Back to History
              </Button>
              <Flex align="center" gap="2">
                <Text size="2" color="gray">
                  Job #{viewingJobLog.id}
                </Text>
                {renderStatusBadge(viewingJobLog.status)}
              </Flex>
            </Flex>

            <Card
              style={{
                padding: "var(--space-3)",
                flex: 1,
                display: "flex",
                flexDirection: "column",
              }}
            >
              <Flex direction="column" gap="2" style={{ height: "100%" }}>
                <Flex justify="between" align="center">
                  <Text size="2" weight="bold">
                    Execution Log
                  </Text>
                  {(viewingJobLog.status === "Pending" ||
                    viewingJobLog.status === "Processing") && (
                    <Flex align="center" gap="2">
                      <Loader2
                        className="animate-spin"
                        size={14}
                        color="var(--blue-9)"
                      />
                      <Text size="1" color="blue">
                        Streaming logs...
                      </Text>
                    </Flex>
                  )}
                </Flex>

                <ScrollArea
                  scrollbars="vertical"
                  style={{
                    flex: 1,
                    backgroundColor: "var(--gray-3)",
                    borderRadius: "var(--radius-2)",
                    padding: "var(--space-3)",
                  }}
                >
                  <pre
                    style={{
                      margin: 0,
                      fontFamily: "var(--font-mono)",
                      fontSize: "12px",
                      lineHeight: "1.5",
                      whiteSpace: "pre-wrap",
                      color: "var(--gray-12)",
                    }}
                  >
                    {viewingJobLog.log || "No log records available yet."}
                    {viewingJobLog.error_message &&
                      `\n\nERROR: ${viewingJobLog.error_message}`}
                  </pre>
                </ScrollArea>
              </Flex>
            </Card>
          </Flex>
        ) : (
          <Tabs.Root value={tabValue} onValueChange={setTabValue}>
            <Tabs.List>
              <Tabs.Trigger value="history">
                <Flex align="center" gap="2">
                  <HistoryIcon size={14} />
                  History
                </Flex>
              </Tabs.Trigger>
              <Tabs.Trigger value="templates">
                <Flex align="center" gap="2">
                  <FileCode size={14} />
                  Templates
                </Flex>
              </Tabs.Trigger>
              <Tabs.Trigger value="new">
                <Flex align="center" gap="2">
                  <FileSpreadsheet size={14} />
                  New Import
                </Flex>
              </Tabs.Trigger>
            </Tabs.List>

            <Box pt="4">
              <Tabs.Content value="history">
                {isLoadingJobs && jobs.length === 0 ? (
                  <Flex justify="center" align="center" py="8" gap="2">
                    <Loader2 className="animate-spin" size={20} />
                    <Text>Loading history...</Text>
                  </Flex>
                ) : jobs.length === 0 ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py="8"
                    gap="2"
                  >
                    <Clock size={36} color="var(--gray-8)" />
                    <Text size="2" color="gray">
                      No import history found for this entity.
                    </Text>
                  </Flex>
                ) : (
                  <ScrollArea scrollbars="vertical" style={{ maxHeight: 380 }}>
                    <Table.Root variant="surface">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeaderCell>
                            Status
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell>
                            Progress
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell>Date</Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell>
                            Actions
                          </Table.ColumnHeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {jobs.map((job) => {
                          const hasProgress = job.total_records != null;
                          return (
                            <Table.Row key={job.id}>
                              <Table.Cell>
                                {renderStatusBadge(job.status)}
                              </Table.Cell>
                              <Table.Cell>
                                {hasProgress ? (
                                  <Flex direction="column" gap="1">
                                    <Text size="1">
                                      {job.processed_records || 0} /{" "}
                                      {job.total_records}
                                    </Text>
                                    {job.failed_records ? (
                                      <Text size="1" color="red">
                                        ({job.failed_records} failed)
                                      </Text>
                                    ) : null}
                                  </Flex>
                                ) : (
                                  <Text size="1" color="gray">
                                    --
                                  </Text>
                                )}
                              </Table.Cell>
                              <Table.Cell>
                                <Text size="1">
                                  {new Date(job.created_at).toLocaleString()}
                                </Text>
                              </Table.Cell>
                              <Table.Cell>
                                <Button
                                  size="1"
                                  variant="ghost"
                                  onClick={async () => {
                                    try {
                                      const detailedJob =
                                        await importService.getJobById(job.id);
                                      setViewingJobLog(detailedJob);
                                    } catch (err) {
                                      toast.error("Failed to load job logs");
                                    }
                                  }}
                                >
                                  View Logs
                                </Button>
                              </Table.Cell>
                            </Table.Row>
                          );
                        })}
                      </Table.Body>
                    </Table.Root>
                  </ScrollArea>
                )}
              </Tabs.Content>

              <Tabs.Content value="templates">
                {isLoadingMappings && mappings.length === 0 ? (
                  <Flex justify="center" align="center" py="8" gap="2">
                    <Loader2 className="animate-spin" size={20} />
                    <Text>Loading templates...</Text>
                  </Flex>
                ) : mappings.length === 0 ? (
                  <Flex
                    direction="column"
                    align="center"
                    justify="center"
                    py="8"
                    gap="2"
                  >
                    <FileCode size={36} color="var(--gray-8)" />
                    <Text size="2" color="gray">
                      No saved mapping templates.
                    </Text>
                  </Flex>
                ) : (
                  <ScrollArea scrollbars="vertical" style={{ maxHeight: 380 }}>
                    <Table.Root variant="surface">
                      <Table.Header>
                        <Table.Row>
                          <Table.ColumnHeaderCell>
                            Template Name
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell>
                            Last Updated
                          </Table.ColumnHeaderCell>
                          <Table.ColumnHeaderCell>
                            Actions
                          </Table.ColumnHeaderCell>
                        </Table.Row>
                      </Table.Header>
                      <Table.Body>
                        {mappings.map((mapping) => (
                          <Table.Row key={mapping.id}>
                            <Table.RowHeaderCell>
                              {mapping.name}
                            </Table.RowHeaderCell>
                            <Table.Cell>
                              <Text size="1">
                                {new Date(mapping.updated_at).toLocaleString()}
                              </Text>
                            </Table.Cell>
                            <Table.Cell>
                              <IconButton
                                size="1"
                                variant="ghost"
                                color="red"
                                onClick={() => handleDeleteMapping(mapping.id)}
                              >
                                <Trash2 size={14} />
                              </IconButton>
                            </Table.Cell>
                          </Table.Row>
                        ))}
                      </Table.Body>
                    </Table.Root>
                  </ScrollArea>
                )}
              </Tabs.Content>

              <Tabs.Content value="new">
                {newImportStep === "upload" ? (
                  <Flex direction="column" gap="3">
                    {uploadError && (
                      <Box
                        style={{
                          backgroundColor: "var(--red-2)",
                          border: "1px solid var(--red-6)",
                          padding: "var(--space-2)",
                          borderRadius: "var(--radius-2)",
                        }}
                      >
                        <Flex gap="2" align="center">
                          <AlertCircle size={16} color="var(--red-9)" />
                          <Text size="2" color="red">
                            {uploadError}
                          </Text>
                        </Flex>
                      </Box>
                    )}
                    <FileUploadZone
                      accept=".csv"
                      maxSize={50}
                      multiple={false}
                      loading={uploading}
                      progress={uploadProgress}
                      progressLabel="Reading headers & uploading CSV file..."
                      onFileChange={handleFileChange}
                    />
                  </Flex>
                ) : (
                  <Flex direction="column" gap="4">
                    <Flex justify="between" align="center">
                      <Flex align="center" gap="2">
                        <Text size="2" weight="bold">
                          File:
                        </Text>
                        <Text size="2" color="gray">
                          {selectedFile?.name} (
                          {formatFileSize(selectedFile?.size || 0)})
                        </Text>
                      </Flex>
                      <Button
                        variant="ghost"
                        size="1"
                        color="red"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileId(null);
                          setNewImportStep("upload");
                        }}
                      >
                        Change File
                      </Button>
                    </Flex>

                    {/* Template Selector & Automatch */}
                    <Flex justify="between" align="end" gap="4">
                      <Flex direction="column" gap="1" style={{ flex: 1 }}>
                        <Text size="1" weight="medium" color="gray">
                          Apply Saved Template
                        </Text>
                        <Select.Root
                          value={selectedTemplateId}
                          onValueChange={handleTemplateSelect}
                        >
                          <Select.Trigger style={{ width: "100%" }} />
                          <Select.Content>
                            <Select.Item value="custom">
                              Custom mapping
                            </Select.Item>
                            {mappings.map((m) => (
                              <Select.Item key={m.id} value={m.id.toString()}>
                                {m.name}
                              </Select.Item>
                            ))}
                          </Select.Content>
                        </Select.Root>
                      </Flex>

                      <Button
                        variant="outline"
                        size="2"
                        onClick={handleAutoMatch}
                      >
                        <Sparkles size={14} />
                        Auto-Match
                      </Button>
                    </Flex>

                    {/* Mapping Table */}
                    <Card style={{ padding: 0 }}>
                      <ScrollArea
                        scrollbars="vertical"
                        style={{ maxHeight: 240, padding: "var(--space-3)" }}
                      >
                        <Grid
                          columns="2"
                          gap="3"
                          align="center"
                          mb="2"
                          style={{
                            borderBottom: "1px solid var(--gray-5)",
                            paddingBottom: "var(--space-2)",
                          }}
                        >
                          <Text size="1" weight="bold" color="gray">
                            Entity Field
                          </Text>
                          <Text size="1" weight="bold" color="gray">
                            CSV Header
                          </Text>
                        </Grid>

                        <Flex direction="column" gap="3">
                          {eligibleFields.map((field) => {
                            const isRequired = field.is_required;
                            const currentMapping =
                              fieldMappings[field.name] || "__skip__";

                            return (
                              <Grid
                                columns="2"
                                gap="3"
                                align="center"
                                key={field.name}
                              >
                                <Flex align="center" gap="1">
                                  <Text size="2" weight="medium">
                                    {field.label || field.name}
                                  </Text>
                                  {isRequired && (
                                    <Text size="2" color="red">
                                      *
                                    </Text>
                                  )}
                                  <Text
                                    size="1"
                                    color="gray"
                                    mr="2"
                                    style={{ marginLeft: "auto" }}
                                  >
                                    ({field.database_type})
                                  </Text>
                                </Flex>

                                <Select.Root
                                  value={currentMapping}
                                  onValueChange={(val) =>
                                    handleMappingChange(field.name, val)
                                  }
                                >
                                  <Select.Trigger style={{ width: "100%" }} />
                                  <Select.Content>
                                    <Select.Item value="__skip__">
                                      <Text color="gray">
                                        -- Ignore / Skip --
                                      </Text>
                                    </Select.Item>
                                    {csvHeaders.map((header) => (
                                      <Select.Item key={header} value={header}>
                                        {header}
                                      </Select.Item>
                                    ))}
                                  </Select.Content>
                                </Select.Root>
                              </Grid>
                            );
                          })}
                        </Flex>
                      </ScrollArea>
                    </Card>

                    {/* Save Template Option */}
                    <Flex direction="column" gap="2">
                      <Flex align="center" gap="2">
                        <Switch
                          checked={saveAsTemplate}
                          onCheckedChange={setSaveAsTemplate}
                        />
                        <Text size="2">Save these mappings as a template</Text>
                      </Flex>
                      {saveAsTemplate && (
                        <TextField.Root
                          placeholder="e.g. My Custom CSV Template"
                          value={templateName}
                          onChange={(e) => setTemplateName(e.target.value)}
                        />
                      )}
                    </Flex>

                    {/* Submit Button */}
                    <Flex justify="end" gap="3">
                      <Button
                        variant="soft"
                        color="gray"
                        onClick={() => {
                          setSelectedFile(null);
                          setFileId(null);
                          setNewImportStep("upload");
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        loading={isRunningImport}
                        disabled={isRunningImport}
                        onClick={handleRunImport}
                      >
                        <Play size={14} />
                        Run Import
                      </Button>
                    </Flex>
                  </Flex>
                )}
              </Tabs.Content>
            </Box>
          </Tabs.Root>
        )}

        <Flex justify="end" mt="4">
          <Dialog.Close>
            <Button variant="soft" color="gray">
              Close
            </Button>
          </Dialog.Close>
        </Flex>
      </Dialog.Content>
    </Dialog.Root>
  );
}
